import { useState, useEffect, useCallback } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import {
  LayoutDashboard,
  Users,
  Truck,
  AlertTriangle,
  BarChart2,
  UserCheck,
  Leaf,
  LogOut,
  Bell,
  Search,
  Menu,
  X,
  ChevronDown,
  CreditCard,
  Settings,
  Send,
  CalendarCheck,
  Star,
  Banknote,
  MessageSquare,
  ClipboardList,
  MapPin,
  Shield,
  Building2,
  ClipboardCheck,
  Activity,
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import api from '../services/api'

/**
 * `companyAdmin: true` marks the handful of destinations a company operator can
 * reach. Everything else is platform-wide and stays with SmartWasteCycle staff.
 */
const NAV_ITEMS = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, end: true, superAdminOnly: false, companyAdmin: true },
  { path: '/users', label: 'Users', icon: Users, end: false, superAdminOnly: true, companyAdmin: false },
  { path: '/map', label: 'Map', icon: MapPin, end: false, superAdminOnly: false, companyAdmin: false },
  { path: '/pickups', label: 'Pickups', icon: Truck, end: false, superAdminOnly: false, companyAdmin: false },
  { path: '/assignments', label: 'Assignments', icon: ClipboardCheck, end: false, superAdminOnly: false, companyAdmin: false },
  { path: '/reports', label: 'Reports', icon: AlertTriangle, end: false, superAdminOnly: false, companyAdmin: false },
  { path: '/analytics', label: 'Analytics', icon: BarChart2, end: false, superAdminOnly: false, companyAdmin: false },
  { path: '/collectors', label: 'Collectors', icon: UserCheck, end: false, superAdminOnly: false, companyAdmin: false },
  { path: '/companies', label: 'Companies', icon: Building2, end: false, superAdminOnly: false, companyAdmin: false },
  { path: '/payments', label: 'Payments', icon: CreditCard, end: false, superAdminOnly: false, companyAdmin: false },
  { path: '/donations', label: 'Donations', icon: Leaf, end: false, superAdminOnly: false, companyAdmin: false },
  { path: '/payouts', label: 'Payouts', icon: Banknote, end: false, superAdminOnly: false, companyAdmin: false },
  { path: '/subscriptions', label: 'Subscriptions', icon: CalendarCheck, end: false, superAdminOnly: false, companyAdmin: false },
  { path: '/ratings', label: 'Ratings', icon: Star, end: false, superAdminOnly: false, companyAdmin: false },
  { path: '/support', label: 'Support', icon: MessageSquare, end: false, superAdminOnly: false, companyAdmin: false },
  { path: '/notifications', label: 'Notifications', icon: Send, end: false, superAdminOnly: false, companyAdmin: false },
  { path: '/audit-log', label: 'Audit Log', icon: ClipboardList, end: false, superAdminOnly: true, companyAdmin: false },
  // Super Admin only: latency percentiles and raw log entries are platform-wide
  // and carry data from every zone, which would sidestep the zone scoping
  // applied everywhere else in the panel.
  { path: '/system-health', label: 'System Health', icon: Activity, end: false, superAdminOnly: true, companyAdmin: false },
  // Reachable by a company operator so they can change their own password —
  // the only way an administrator-issued credential stops being one.
  { path: '/settings', label: 'Settings', icon: Settings, end: false, superAdminOnly: true, companyAdmin: true },
]

const PAGE_TITLES: Record<string, string> = {
  '/': 'Dashboard Overview',
  '/users': 'User Management',
  '/map': 'Operations Map',
  '/pickups': 'Pickup Monitoring',
  '/assignments': 'Pickup Offer Queue',
  '/reports': 'Illegal Dumping Reports',
  '/analytics': 'Analytics & Insights',
  '/collectors': 'Collector Management',
  '/companies': 'Waste Collection Companies',
  '/payments': 'Payments & Transactions',
  '/donations': 'Green City Donations',
  '/payouts': 'Collector Payouts',
  '/subscriptions': 'Subscription Management',
  '/ratings': 'Ratings & Reviews',
  '/support': 'Support Conversations',
  '/notifications': 'Notification Management',
  '/audit-log': 'Audit Log',
  '/settings': 'Settings',
}

interface Notification {
  id: string
  title: string
  body: string
  isRead: boolean
  createdAt: string
}

export default function Layout() {
  const { user, adminLogout } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Live notifications
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications?limit=5')
      const data = res.data?.data ?? res.data
      setNotifications(data?.notifications ?? [])
      setUnreadCount(data?.unreadCount ?? 0)
    } catch {
      // Non-critical — don't disrupt the UI
    }
  }, [])

  useEffect(() => {
    fetchNotifications()
    const interval = setInterval(fetchNotifications, 60000) // poll every 60s
    return () => clearInterval(interval)
  }, [fetchNotifications])

  const handleMarkAllRead = async () => {
    try {
      await api.patch('/notifications/read-all')
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })))
      setUnreadCount(0)
    } catch {
      // Silent
    }
  }

  const isCompanyAdmin = user?.role === 'COMPANY_ADMIN'
  const pageTitle = isCompanyAdmin
    ? (user?.companyName ?? 'My Company')
    : PAGE_TITLES[location.pathname] ?? 'Admin Panel'

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      // Route to the most relevant page with search context
      if (q.includes('support') || q.includes('chat') || q.includes('help')) {
        navigate('/support')
      } else if (q.includes('donat') || q.includes('green') || q.includes('tree') || q.includes('campaign')) {
        navigate('/donations')
      } else if (q.includes('pay') || q.includes('transaction') || q.includes('revenue')) {
        navigate('/payments')
      } else if (q.includes('report') || q.includes('dump')) {
        navigate('/reports')
      } else if (q.includes('pickup') || q.includes('collect')) {
        navigate('/pickups')
      } else if (user?.isSuperAdmin) {
        navigate(`/users?search=${encodeURIComponent(searchQuery.trim())}`)
      } else {
        // Users is super-admin-only; a zone admin's best match for a name is the pickups list.
        navigate(`/pickups?search=${encodeURIComponent(searchQuery.trim())}`)
      }
      setSearchQuery('')
    }
  }

  function timeAgo(dateStr: string) {
    const diff = Date.now() - new Date(dateStr).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    return `${Math.floor(hrs / 24)}d ago`
  }

  return (
    <div className="flex h-screen bg-[#F8FAF9] overflow-hidden">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: { borderRadius: '12px', fontSize: '13px' },
          success: { iconTheme: { primary: '#2D7D46', secondary: '#fff' } },
        }}
      />

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-30 w-60 flex flex-col bg-white border-r border-gray-100 shadow-sm transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center gap-2.5 px-5 py-5 border-b border-gray-100">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary-600">
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-[#1A1A2E] leading-tight">SmartWasteCycle</p>
            <p className="text-[10px] text-[#6B7280] font-medium uppercase tracking-wide">Admin Console</p>
          </div>
          <button
            className="ml-auto lg:hidden text-gray-400 hover:text-gray-600"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          <p className="px-3 text-[10px] font-semibold text-[#6B7280] uppercase tracking-widest mb-3">
            Main Menu
          </p>
          {NAV_ITEMS.filter((item) =>
            user?.role === 'COMPANY_ADMIN'
              ? item.companyAdmin
              : !item.superAdminOnly || user?.isSuperAdmin,
          ).map(({ path, label, icon: Icon, end }) => (
            <NavLink
              key={path}
              to={path}
              end={end}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-[#6B7280] hover:bg-primary-50 hover:text-primary-700'
                }`
              }
            >
              <Icon className="w-4.5 h-4.5 flex-shrink-0" size={18} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div className="px-3 py-4 border-t border-gray-100">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-bold">
              {user?.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-semibold text-[#1A1A2E] truncate">{user?.name}</p>
                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
                  isCompanyAdmin
                    ? 'bg-emerald-100 text-emerald-700'
                    : user?.isSuperAdmin
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-100 text-gray-600'
                }`}>
                  <Shield size={9} />
                  {isCompanyAdmin ? 'Company' : user?.isSuperAdmin ? 'Super' : 'Admin'}
                </span>
              </div>
              <p className="text-[10px] text-[#6B7280] truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={adminLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
          >
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header */}
        <header className="flex items-center gap-4 px-6 py-4 bg-white border-b border-gray-100 shadow-sm z-10">
          <button
            className="lg:hidden text-gray-500 hover:text-gray-700"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>

          <h1 className="text-lg font-bold text-[#1A1A2E] hidden sm:block">{pageTitle}</h1>

          {/* Search */}
          {!isCompanyAdmin && (
<div className="flex-1 max-w-md ml-4 hidden md:flex items-center gap-2 bg-[#F8FAF9] border border-gray-200 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-[#6B7280]" />
            <input
              type="text"
              placeholder="Search users, pickups, reports..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearch}
              className="flex-1 bg-transparent text-sm text-[#1A1A2E] placeholder-[#6B7280] outline-none"
            />
          </div>
)}

          <div className="ml-auto flex items-center gap-3">
            {/* Notifications */}
            <div className="relative">
              <button
                className="relative p-2 rounded-xl text-[#6B7280] hover:bg-gray-100 transition-colors"
                onClick={() => { setNotificationsOpen(!notificationsOpen); setProfileOpen(false) }}
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-accent text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-100 rounded-2xl shadow-xl z-50">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <p className="font-semibold text-sm text-[#1A1A2E]">Notifications</p>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-primary-600 font-semibold hover:underline"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="divide-y divide-gray-50 max-h-72 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-6 text-center text-xs text-[#6B7280]">No notifications</div>
                    ) : (
                      notifications.map((n) => (
                        <div key={n.id} className={`px-4 py-3 ${!n.isRead ? 'bg-primary-50/40' : ''}`}>
                          <p className="text-xs text-[#1A1A2E] font-medium">{n.title}</p>
                          <p className="text-[10px] text-[#6B7280] mt-0.5 line-clamp-1">{n.body}</p>
                          <p className="text-[10px] text-[#6B7280] mt-0.5">{timeAgo(n.createdAt)}</p>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="px-4 py-2.5 border-t border-gray-100">
                    {!isCompanyAdmin && (
                      <button
                        onClick={() => { navigate('/notifications'); setNotificationsOpen(false) }}
                        className="text-xs text-primary-600 font-semibold hover:underline"
                      >
                        View all notifications
                      </button>
                    )}
                    {isCompanyAdmin && (
                      <p className="text-[10px] text-[#6B7280]">Your latest five notifications are shown here.</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Profile */}
            <div className="relative">
              <button
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-xl hover:bg-gray-100 transition-colors"
                onClick={() => { setProfileOpen(!profileOpen); setNotificationsOpen(false) }}
              >
                <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-bold">
                  {user?.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <span className="text-sm font-medium text-[#1A1A2E] hidden sm:block">{user?.name}</span>
                <ChevronDown className="w-3.5 h-3.5 text-[#6B7280] hidden sm:block" />
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 rounded-xl shadow-xl z-50 py-1">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-xs font-semibold text-[#1A1A2E]">{user?.name}</p>
                    <p className="text-[10px] text-[#6B7280]">{user?.email}</p>
                  </div>
                  <button
                    onClick={() => { navigate('/settings'); setProfileOpen(false) }}
                    className="w-full text-left px-4 py-2 text-xs text-[#1A1A2E] hover:bg-gray-50"
                  >
                    Profile Settings
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 text-xs text-red-500 hover:bg-red-50"
                    onClick={adminLogout}
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
